import { Flex, Modal, Radio, Space, Spin, Table, TableColumnsType, Typography } from 'antd';
import { StyledButton } from 'src/styled_components/StyledButton';
import { StoreState } from 'src/store/configureStore';
import { useDispatch, useSelector } from 'react-redux';
import { Samples } from 'src/mocks/db';
import {
  AsyncStates,
  defaultHeaders
} from 'src/constants';
import {
  DownloadOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { useToken } from 'src/utils/hooks/useToken';
import { SavedModelsTable } from './Analysis';
import { useEffect, useMemo, useState } from 'react';
import { selectedModelsRequest } from 'src/store/actions/operations';

interface DataType {
  key: React.Key;
  name: string;
  age: number;
  address: string;
}

export const Results = ({ currentStep, previous, next, steps }: any) => {

  const dispatch = useDispatch()
  const { anid } = useParams<any>()

  const evaluationStatus = useSelector((state: StoreState) => state.operations.modelEvaluationStatus)
  const evaluationData = useSelector((state: StoreState) => state.operations.modelEvaluationData)
  
  const token = useToken()
  const target_ids = useMemo(() => {
    const targetIds = evaluationData?.prediction?.[0]?.target_ids || [];
    return targetIds.map((id: string) => {
      const sample = Samples.findOne({ id });
      return sample ? sample.name : id;
    });
  }, [evaluationData]);

  const fetchReport = async () => {
    const url = `${process.env.REACT_APP_MODEL_PERMUTATION_API}/results/${anid}/download-report?model_id=${reportModelId}`;
    const feature_importance_ids: any = savedModels.find((model: any) => model.model_id === reportModelId)?.feature_importance;
    let feature_importance: any = []
    for (const feature of feature_importance_ids) {
      const featureData = Samples.findOne({ id: feature.feature_id });
      if (featureData) {
        feature_importance.push({ ...feature, feature_id: featureData?.name ?? feature.feature_id });
      } else {
        feature_importance.push(feature)
      }
    }
    fetch(url, {
      method: "POST",
      headers: { ...defaultHeaders, token },
      body: JSON.stringify({ model_id: reportModelId, feature_importance, evaluation_id: evaluationData?.evaluation_id, target_ids }) // Include model_id in the request body 
    })
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP error! Status: ${res.status}`);
        }
        // Response is a stream
        return res.blob(); // Convert response to a Blob for file handling
      })
      .then(blob => {
        // Create a download link for the file
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;

        // File name for download
        a.download = `${anid}-report.xlsx`; // Use the desired filename
        document.body.appendChild(a);
        a.click();

        // Clean up
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setShowModelSelection(false); // Close the modal after download
      })
      .catch(err => {
        console.error("Error downloading file:", err);
      });
  }

  useEffect(() => {
    dispatch(selectedModelsRequest(anid));
  }, [anid, dispatch])

  const savedModels = useSelector((state: StoreState) => state.operations.selectedModelsData)

  const [showModelSelection, setShowModelSelection] = useState<boolean>(false);

  const [reportModelId, setReportModelId] = useState<string | undefined>(savedModels?.[0]?.model_id);

  useEffect(() => {
    if (savedModels?.length) {
      setReportModelId(savedModels[0].model_id)
    }
  }, [savedModels])

  return (
    <Spin spinning={evaluationStatus === AsyncStates.LOADING} indicator={<LoadingOutlined />}>
      <Space direction="vertical" style={{ width: "100%" }}>
        <Typography.Title level={5}>Model Testing</Typography.Title>
        <Typography.Text type='secondary'>With the selected Categories, Parameters and Sub Categories model evaluation is being generated</Typography.Text><br />
        <Flex justify='flex-end'>
          <StyledButton icon={<DownloadOutlined />} size="small" type='primary' onClick={() => { setShowModelSelection(true) }}>Report</StyledButton>
        </Flex>
        <ResultsTable evaluationData={evaluationData} />
        <SavedModelsTable />
        <Flex style={{ marginTop: 24 }} justify="end">
          <div>
            {currentStep > 0 && (
              <StyledButton type='primary' size="small" style={{ margin: '0 8px' }} onClick={previous}>
                Re-evaluate
              </StyledButton>
            )}
          </div>
          <Space>
            {/* {(
                        <StyledButton size="small" onClick={save}>
                          Save
                        </StyledButton>
                      )} */}
            {/* <StyledButton size="small" type="primary" onClick={() => { }}>
              Finish Analysis
            </StyledButton> */}
          </Space>
        </Flex>
        <Modal open={showModelSelection} title="Select Model for Report" onCancel={() => setShowModelSelection(false)} onOk={fetchReport} okText="Download" cancelText="Cancel">
          <Radio.Group
            value={reportModelId}
            options={savedModels.map((model: any) => (
              { label: `${model.category_combo} [${model.model_type}]`, value: model.model_id }))}
            onChange={(e) => setReportModelId(e.target.value)}
          />
        </Modal>
      </Space>
    </Spin>
  )
}

export const ResultsTable = ({ evaluationData }: any) => {
  const columns: TableColumnsType<DataType> = [
    {
      title: 'Category Combo',
      dataIndex: 'combo',
      fixed: true,
      width: 500,
    },
    {
      title: 'Model Type',
      dataIndex: 'model',
      fixed: true
    },
    {
      title: 'Actual',
      children: [...(new Set(evaluationData?.prediction?.flatMap((item: any) => item?.target_ids)))]
        ?.map((target: any) => ({
          title: Samples.findOne({ id: target })?.name || `Target ${target}`,
          dataIndex: ['actual', target],
        }))
    },
    {
      title: 'Predicted',
      children: [...(new Set(evaluationData?.prediction?.flatMap((item: any) => item?.target_ids)))]
        ?.map((target: any) => ({
          title: Samples.findOne({ id: target })?.name || `Target ${target}`,
          dataIndex: ['predicted', target],
          render: (text: any) => text?.toFixed ? text?.toFixed(3) : text
        }))
    },
    {
      title: 'R2',
      dataIndex: 'r2',
      render: (text: string) => text || "N/A"
    },
    {
      title: 'RMSE',
      dataIndex: 'rmse',
      render: (text: string) => text || "N/A"
    },
    {
      title: 'BIC',
      dataIndex: 'bic',
      render: (text: string) => text || "N/A"
    },
    {
      title: 'Error',
      children: [...(new Set(evaluationData?.prediction?.flatMap((item: any) => item?.target_ids)))]
        ?.map((target: any) => ({
          title: Samples.findOne({ id: target })?.name || `Target ${target}`,
          dataIndex: ['error', target],
        }))
    },
  ];

  const tableData = useMemo(() => evaluationData?.prediction?.flatMap((item: any) => item.y?.map((_: any, k: number) => {
    const actual_map = item.target_ids.reduce((a: any, target: string, i: number) => ({ ...a, [target]: item.y[k][i] }), {})
    const predicted_map = item.target_ids.reduce((a: any, target: string, i: number) => ({ ...a, [target]: item.y_pred[k][i] }), {})
    const target_ids = item?.target_ids; // Array of target IDs
    const combo = item?.combo;
    const model_type = item?.model_type;
    const error_map = item.target_ids.reduce(
      (a: any, target: string, i: number) => ({
        ...a,
        [target]: `${(((Math.abs(item.y[k][i] - item.y_pred[k][i])) / item.y[k][i]) * 100)?.toFixed(2)}%`, // Error = actual - predicted
      }),
      {}
    );
    const r2 = item?.r2 ? item?.r2?.toFixed(3) : "N/A";
    const rmse = item?.rmse ? item?.rmse?.toFixed(3) : "N/A";
    const bic = item?.bic ? item?.bic?.toFixed(3) : "N/A";

    return {
      key: combo + model_type + k, // Unique key for each row
      combo,
      model: model_type,
      target_id: target_ids,
      actual: actual_map,
      predicted: predicted_map,
      error: error_map,
      r2,
      rmse,
      bic,
    };
  })), [evaluationData]);

  return <Table
    key={"results_" + evaluationData?.id}
    scroll={{ x: 'max-content' }}
    rowSelection={{ }}
    columns={columns}
    dataSource={tableData}
  />
}
